import React, { useCallback, useState } from 'react';
import { motion } from 'framer-motion';
import { Upload, Film, Music, FileVideo, FileAudio, Sparkles, MessageSquare, Mic } from 'lucide-react';

interface FileUploadProps {
  onFileUpload: (file: File) => void;
}

const FileUpload: React.FC<FileUploadProps> = ({ onFileUpload }) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    const mediaFile = files.find(file => 
      file.type.startsWith('video/') || file.type.startsWith('audio/')
    );
    
    if (mediaFile) {
      setIsUploading(true);
      setTimeout(() => {
        onFileUpload(mediaFile);
        setIsUploading(false);
      }, 1000);
    }
  }, [onFileUpload]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && (file.type.startsWith('video/') || file.type.startsWith('audio/'))) {
      setIsUploading(true);
      setTimeout(() => {
        onFileUpload(file);
        setIsUploading(false);
      }, 1000);
    }
  }, [onFileUpload]);

  return (
    <div className="w-full max-w-2xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="text-center mb-8"
      >
        <div className="flex items-center justify-center mb-4">
          <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mr-4">
            <Sparkles className="w-8 h-8 text-black" />
          </div>
          <div className="text-left">
            <h2 className="text-3xl font-bold text-white mb-2">AI Media Editor</h2>
            <p className="text-gray-400">Upload your audio or video file to start editing with AI</p>
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className={`
          relative border-2 border-dashed rounded-xl p-12 text-center transition-all duration-300
          ${isDragOver 
            ? 'border-white bg-gray-900/50 scale-105' 
            : 'border-gray-600 hover:border-gray-500 hover:bg-gray-900/30'
          }
          ${isUploading ? 'border-white bg-gray-900/50' : ''}
        `}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input
          type="file"
          accept="video/*,audio/*"
          onChange={handleFileSelect}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          disabled={isUploading}
        />
        
        {isUploading ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex flex-col items-center"
          >
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
              className="w-16 h-16 border-4 border-gray-600 border-t-white rounded-full mb-4"
            />
            <p className="text-white font-medium">Processing your file...</p>
            <p className="text-gray-400 text-sm mt-1">This may take a moment</p>
          </motion.div>
        ) : (
          <motion.div
            animate={{ y: isDragOver ? -5 : 0 }}
            transition={{ duration: 0.2 }}
            className="flex flex-col items-center"
          >
            <div className="flex items-center justify-center space-x-4 mb-6">
              <motion.div
                whileHover={{ scale: 1.1 }}
                className="w-12 h-12 bg-gray-800 rounded-lg flex items-center justify-center"
              >
                <FileVideo className="w-6 h-6 text-white" />
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.1 }}
                className="w-12 h-12 bg-gray-800 rounded-lg flex items-center justify-center"
              >
                <FileAudio className="w-6 h-6 text-white" />
              </motion.div>
            </div>
            
            <Upload className="w-12 h-12 text-gray-400 mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">
              {isDragOver ? 'Drop your file here' : 'Upload Media File'}
            </h3>
            <p className="text-gray-400 mb-4">
              Drag and drop your video or audio file, or click to browse
            </p>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-white text-black px-6 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors"
            >
              Choose File
            </motion.button>
            
            <div className="mt-6 text-sm text-gray-500">
              <p>Supported formats:</p>
              <div className="flex items-center justify-center space-x-4 mt-2">
                <span className="flex items-center space-x-1">
                  <Film className="w-4 h-4" />
                  <span>MP4, AVI, MOV</span>
                </span>
                <span className="w-px h-4 bg-gray-600"></span>
                <span className="flex items-center space-x-1">
                  <Music className="w-4 h-4" />
                  <span>MP3, WAV, AAC</span>
                </span>
              </div>
            </div>
          </motion.div>
        )}
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.4 }}
        className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 text-center"
      >
        <div className="bg-gray-900/50 rounded-lg p-4 border border-gray-800">
          <MessageSquare className="w-6 h-6 text-white mx-auto mb-2" />
          <h4 className="font-medium text-white mb-1">Chat Commands</h4>
          <p className="text-sm text-gray-400">Edit with natural language</p>
        </div>
        
        <div className="bg-gray-900/50 rounded-lg p-4 border border-gray-800">
          <Mic className="w-6 h-6 text-white mx-auto mb-2" />
          <h4 className="font-medium text-white mb-1">Voice Control</h4>
          <p className="text-sm text-gray-400">Speak your editing commands</p>
        </div>
        
        <div className="bg-gray-900/50 rounded-lg p-4 border border-gray-800">
          <Sparkles className="w-6 h-6 text-white mx-auto mb-2" />
          <h4 className="font-medium text-white mb-1">AI Processing</h4>
          <p className="text-sm text-gray-400">Intelligent media enhancement</p>
        </div>
      </motion.div>
    </div>
  );
};

export default FileUpload;